import { useCallback } from "react";
import { useNotifications } from "../components/common";

interface ApiError {
  statusCode: number;
  message: string | string[];
  error: string;
  timestamp: string;
  path: string;
  method: string;
}

export const useApiError = () => {
  const { showError } = useNotifications();

  const handleApiError = useCallback(
    (error: any) => {
      console.error("API Error:", error);

      // Handle network errors
      if (!error.response) {
        showError(
          "Errore di connessione",
          "Impossibile connettersi al server. Controlla la tua connessione internet."
        );
        return;
      }

      const { status, data } = error.response;
      const apiError: ApiError = data;

      // Handle different error types
      switch (status) {
        case 400:
          handleBadRequest(apiError);
          break;
        case 401:
          handleUnauthorized(apiError);
          break;
        case 403:
          handleForbidden(apiError);
          break;
        case 404:
          handleNotFound(apiError);
          break;
        case 409:
          handleConflict(apiError);
          break;
        case 422:
          handleValidationError(apiError);
          break;
        case 500:
        default:
          handleServerError(apiError);
          break;
      }
    },
    [showError]
  );

  const handleBadRequest = (error: ApiError) => {
    const message = Array.isArray(error.message)
      ? error.message.join(", ")
      : error.message;

    showError("Richiesta non valida", message);
  };

  const handleUnauthorized = async (error: ApiError) => {
    // Check if we're on a public route first
    const { isPublicRoute } = await import("../constants/routes");
    const currentPath = globalThis.location.pathname;

    if (isPublicRoute(currentPath)) {
      console.log("401 on public route, ignoring:", currentPath);
      return;
    }

    // Show user notification for protected routes
    showError(
      "Accesso non autorizzato",
      "La tua sessione è scaduta. Effettua nuovamente l'accesso."
    );

    // Use the centralized error handler for cleanup and redirect
    try {
      const { errorHandler } = await import("../utils/errorHandler");
      const message = Array.isArray(error.message)
        ? error.message.join(", ")
        : error.message;
      errorHandler.handleAuthError(new Error(message), {
        component: "useApiError",
        action: "unauthorized",
      });
    } catch (importError) {
      console.error("Failed to import errorHandler:", importError);
      // Fallback: redirect manually
      setTimeout(() => {
        globalThis.location.href = "/login";
      }, 2000);
    }
  };

  const handleForbidden = (error: ApiError) => {
    const message =
      typeof error.message === "string"
        ? error.message
        : "Non hai i permessi necessari per questa operazione.";

    showError("Accesso negato", message);
  };

  const handleNotFound = (error: ApiError) => {
    const message =
      typeof error.message === "string"
        ? error.message
        : "La risorsa richiesta non è stata trovata.";

    showError("Non trovato", message);
  };

  const handleConflict = (error: ApiError) => {
    const message =
      typeof error.message === "string"
        ? error.message
        : "Conflitto con lo stato attuale della risorsa.";

    showError("Conflitto", message);
  };

  const handleValidationError = (error: ApiError) => {
    const message = Array.isArray(error.message)
      ? error.message.join(", ")
      : error.message;

    showError("Errore di validazione", message);
  };

  const handleServerError = (error: ApiError) => {
    showError(
      "Errore del server",
      "Si è verificato un errore interno del server. Riprova più tardi."
    );
  };

  return { handleApiError };
};

export default useApiError;
